<template>
  <div class="min-h-screen bg-gray-50 py-6">
    <div class="mx-auto px-4">
      <!-- Header -->
      <v-container class="mb-8">
        <h1 class="text-h4 font-weight-bold mb-2">Giftcard</h1>
        <div class="d-flex align-center">
          <v-btn @click="goBack" icon small color="grey" class="mr-3">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <h2 class="text-h6 font-weight-medium">
            Kindly confirm trade details
          </h2>
        </div>
      </v-container>

      <!-- Preview -->
      <v-dialog persistent v-model="previewDialog" max-width="600">
        <v-card class="relative">
          <v-btn
            icon
            small
            class="close-preview-btn"
            style="position: absolute; top: 8px; right: 8px; z-index: 1"
            :disabled="tradeProgress !== 'idle'"
            @click="previewDialog = false"
            aria-label="Close preview"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-container class="pb-40">
            <v-card-title> Preview Gift Card Sale </v-card-title>
            <v-card-subtitle>
              Please review the details of your gift card sale before
              submitting.
            </v-card-subtitle>

            <v-card-text>
              <div class="grid lg:grid-cols-2 grid-cols-1 gap-4 mt-4">
                <div>
                  <p class="mb-0">Gift Card</p>
                  <p class="font-semibold">{{ cardData.name }}</p>
                </div>
                <div>
                  <p class="mb-0">Gift Card Type</p>
                  <p class="font-semibold">
                    {{ selectedCardTypeData.cardType }}
                  </p>
                </div>
                <div>
                  <p class="mb-0">Gift Card Amount</p>
                  <p class="font-semibold">{{ variationAmount }}</p>
                </div>
                <div>
                  <p class="mb-0">You will receive</p>
                  <p class="font-semibold">{{ calculatedPrice }}</p>
                </div>
                <div>
                  <p class="mb-0">Comments</p>
                  <p class="font-semibold">
                    {{ formData.comments ? formData.comments : 'No comments' }}
                  </p>
                </div>
                <div>
                  <p class="mb-0">Promo Code</p>
                  <p class="font-semibold">
                    {{ promoCode ? promoCode : 'No promo code' }}
                  </p>
                </div>
                <div>
                  <p class="mb-0">Promo Code Bonus</p>
                  <p class="font-semibold">
                    {{
                      promoCodeBonus ? promoCodeBonus : 'No promo code bonus'
                    }}
                  </p>
                </div>
                <div>
                  <p class="mb-0">Cashback Points</p>
                  <p class="font-semibold">
                    {{
                      cashbackPointsToEarn
                        ? cashbackPointsToEarn
                        : 'No cashback points'
                    }}
                  </p>
                </div>
                <div>
                  <p class="mb-0">Average Confirmation time</p>
                  <p class="font-semibold">
                    {{ giftcardResponseTime ? giftcardResponseTime : '5-40' }}
                    Minutes
                  </p>
                </div>
                <div class="col-span-2">
                  <p class="mb-2">Images</p>
                  <div
                    v-if="imagesRender.length > 0"
                    class="flex flex-wrap gap-2"
                  >
                    <img
                      v-for="(img, idx) in imagesRender"
                      :key="idx"
                      :src="img"
                      alt="image"
                      class="w-24 h-24 object-cover rounded-md"
                    />
                  </div>
                  <div v-else>
                    <p class="font-semibold">No images</p>
                  </div>
                </div>
              </div>
            </v-card-text>
          </v-container>
          <div class="absolute bottom-0 left-0 right-0 bg-gray-200">
            <v-container>
              <div class="mb-4">
                <p class="text-center text-sm mb-0">You will receive</p>
                <p class="font-semibold text-2xl text-center mb-0">
                  {{ userCurrency }}
                  {{ calculatedPriceWithBonus }}
                </p>
              </div>
              <v-btn
                color="primary"
                class="d-block mx-auto"
                style="width: 60%"
                :loading="tradeProgress !== 'idle'"
                :disabled="tradeProgress !== 'idle'"
                @click="createSell"
                >Confirm
              </v-btn>
              <p class="text-center text-sm p-0 pt-1">
                {{ confirmButtonText }}
              </p>
            </v-container>
          </div>
        </v-card>
      </v-dialog>

      <!-- Form -->
      <v-container>
        <v-form ref="form" @submit.prevent="handleSubmit">
          <!-- Gift Card List -->
          <v-autocomplete
            v-model="cardId"
            :items="cards"
            item-text="name"
            item-value="_id"
            label="Choose card"
            filled
            rounded
            class="mb-4"
            @change="changeCard"
            :rules="[rules.required]"
            :loading="loading"
          >
            <template v-slot:item="{ item }">
              <v-list-item-avatar tile class="mr-3">
                <v-img
                  :src="item.image"
                  width="40"
                  height="30"
                  contain
                  alt="item.name"
                ></v-img>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title v-text="item.name"></v-list-item-title>
              </v-list-item-content>
            </template>
          </v-autocomplete>

          <!-- Gift Card Category -->
          <v-autocomplete
            v-model="selectedOptionId"
            :items="availableCountry"
            item-text="countryName"
            item-value="id"
            label="Choose country"
            filled
            rounded
            class="mb-4"
            :rules="[rules.required]"
            :loading="gettingCardData"
            :disabled="gettingCardData"
          >
            <template v-slot:item="{ item }">
              <v-list-item-avatar tile class="mr-3">
                <v-img
                  :src="objectImage[item.countryName.toLowerCase()]"
                  width="40"
                  height="30"
                  contain
                  alt="item.name"
                ></v-img>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title
                  v-text="item.countryName"
                ></v-list-item-title>
              </v-list-item-content>
            </template>
          </v-autocomplete>

          <!-- Gift Card card type -->
          <v-autocomplete
            v-model="selectedCardTypeId"
            :items="availableCardTypes"
            item-text="cardType"
            item-value="_id"
            label="Choose card type"
            filled
            rounded
            class="mb-4"
            @change="changeCardType"
            :rules="[rules.required]"
            persistent-hint
            :hint="cardTypeHint"
            :disabled="gettingCardData"
          >
            <template v-slot:item="{ item }">
              <v-list-item-content>
                <v-list-item-title v-text="item.cardType" />
              </v-list-item-content>
            </template>
          </v-autocomplete>

          <!-- Gift Card Amount -->
          <v-text-field
            v-model="variationAmount"
            label="Enter gift card amount"
            placeholder="Enter amount"
            :prefix="amountPrefix"
            filled
            rounded
            class="mb-4"
            :rules="[rules.required]"
            type="number"
            persistent-hint
            :hint="amountInputHint"
            :disabled="gettingCardData"
          ></v-text-field>

          <!-- Amount -->
          <v-text-field
            label="You will receive"
            placeholder="You will receive"
            :prefix="userCurrency"
            filled
            rounded
            class="mb-4"
            disabled
            :value="numberWithCommas(calculatedPrice)"
          ></v-text-field>

          <!-- Comments -->
          <v-textarea
            v-model="formData.comments"
            label="Optional Comments (e.g., E-code Number)"
            placeholder="Enter any additional comments or E-code number"
            filled
            rounded
            rows="4"
            class="mb-4"
            auto-grow
          ></v-textarea>

          <!-- Image Upload -->
          <v-file-input
            v-model="formData.images"
            label="Gift Card Images *"
            placeholder="Upload gift card images"
            :rules="[rules.imagesRequired]"
            filled
            rounded
            multiple
            accept="image/*"
            prepend-icon="mdi-camera"
            class="mb-4"
            show-size
            counter
          >
            <template v-slot:selection="{ text }">
              <v-chip small label color="primary">
                {{ text }}
              </v-chip>
            </template>
          </v-file-input>

          <div v-if="imagesRender.length > 0" class="flex flex-wrap gap-2">
            <div v-for="(img, idx) in imagesRender" :key="idx" class="relative">
              <div
                class="absolute -top-2 -right-2 bg-white rounded-full w-8 h-8 flex items-center justify-center"
                @click="removeImage(idx)"
              >
                <v-icon color="error">mdi-delete</v-icon>
              </div>
              <img
                :src="img"
                alt="image"
                class="w-24 h-24 object-cover rounded-md cursor-pointer"
                @click="removeImage(idx)"
              />
            </div>
          </div>

          <!-- Promo Code -->
          <div>
            <v-checkbox
              v-model="hasPromoCode"
              label="I have a promo code"
            ></v-checkbox>
            <div v-if="hasPromoCode">
              <div class="d-flex align-center mb-2">
                <span class="text-body-2 font-weight-medium mr-2"
                  >Promo code</span
                >
                <span
                  class="border p-1 text-gray-400 text-xs rounded-full border-gray-400"
                  style="cursor: pointer; line-height: 1"
                  role="button"
                  tabindex="0"
                  aria-label="About promo codes"
                  @click="aboutPromoCode"
                  @keyup.enter="aboutPromoCode"
                >
                  ?
                </span>
              </div>
              <v-text-field
                v-model="promoCode"
                label="Enter promo code"
                filled
                rounded
                class="mb-4"
              >
                <template v-slot:append-outer>
                  <v-btn
                    color="primary"
                    @click="applyPromoCode"
                    :loading="loadingPromoCode"
                    :disabled="!promoCode"
                  >
                    Apply
                  </v-btn>
                </template>
              </v-text-field>
            </div>
          </div>

          <!-- Preview Button -->
          <div class="pt-6">
            <v-btn
              type="submit"
              color="primary"
              block
              large
              rounded
              elevation="2"
              class="text-h6 font-weight-medium"
              @click="handleSubmit"
              :loading="tradeProgress !== 'idle'"
              :disabled="tradeProgress !== 'idle'"
            >
              Preview
            </v-btn>
          </div>
        </v-form>
      </v-container>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import dataApi from '../../../../api/systemData'
// import giftcardApi from '../../../../api/users/giftcard'
// import transactionApi from '../../../../api/users/transactions'
import countryFlags from '../../../../utils/countryFlags'
import { numberWithCommas } from '../../../../utils/helpers'
import promoCodes from '../../../../api/promo-codes'
import transactions from '../../../../api/users/transactions'
import giftcard from '../../../../api/users/giftcard'

export default {
  name: 'SellGiftcard',
  data() {
    return {
      selectedType: 'ecode',
      formData: {
        category: '',
        subCategory: '',
        giftCardAmount: '',
        amount: '',
        comments: '',
        images: []
      },
      rules: {
        required: (v) => !!v || 'This field is required',
        imagesRequired: (v) =>
          (Array.isArray(v) && v.length > 0) ||
          'Add at least one gift card image'
      },
      cardId: '',
      cardData: '',
      gettingCardData: false,
      giftcardResponseTime: '',
      selectedOptionId: '',
      selectedCardTypeId: '',
      numberWithCommas,
      variationAmount: '',
      sellCard: {},
      loading: false,
      cards: [],
      hasPromoCode: false,
      promoCode: '',
      loadingPromoCode: false,
      cashbackPointsToEarn: 0,
      promoCodeBonus: 0,
      previewDialog: false,
      images: [],
      imageFiles: [],
      // idle, creating trade, uploading images, completed, idle
      tradeProgress: 'idle'
    }
  },
  async created() {
    this.cardId = this.$route.params.id
    this.getCardData()
    this.getAllGiftcard()
    this.$store.dispatch('user/getCountryCurrency')
    this.getTime()
    this.objectImage = countryFlags
  },
  methods: {
    goBack() {
      this.$router.go(-1)
    },
    async aboutPromoCode() {
      this.$swal.fire({
        icon: 'info',
        title: 'About Promo Codes',
        text:
          'Do you have a promo code? Enter it here to see if you qualify for a trade bonus. If you do not have any, leave it empty. To see available promo codes, check the Promo codes page on our Blog  or follow SnappyExchange on Instagram @SnappyExchangeNg'
      })
    },
    async getAllGiftcard() {
      this.loading = true
      const res = await dataApi.data().getData('user', { type: 'giftcard' })
      this.cards = res.data.data.data.filter((d) => d.status)
      this.loading = false
    },
    changeAvailableCardType() {
      this.selectedCardTypeId = ''
      this.changeCardType()
    },
    async handleSubmit() {
      if (this.$refs.form.validate()) {
        this.previewDialog = true
      }
    },
    changeCard() {
      this.getCardData()
      this.selectedCardTypeId = ''
    },
    changeCardType(data) {
      this.sellCard = {
        quantity: 1,
        variationAmount: this.variationAmount,
        nairaRate: 0
      }
    },
    async getCardData() {
      this.gettingCardData = true
      const res = await dataApi.data().getSingleData('user', {
        type: 'giftcard',
        params: {
          populate: ['options.country', 'options.currency']
        },
        typeId: this.cardId
      })
      this.cardData = res.data.data
      this.gettingCardData = false
    },
    async getTime() {
      const res = await dataApi.data().siteSettings('user')
      this.giftcardResponseTime = res.data.data.giftcardResponseTime
    },
    async applyPromoCode() {
      this.loadingPromoCode = true
      const res = await promoCodes.promoCodes().calculateCodeBonus({
        promoCode: this.promoCode.trim(),
        productType: 'giftcard',
        amount: Number(this.calculatedPriceBase.toString().replace(/,/g, '')),
        productId: this.cardId
      })
      if (res.error) {
        this.$store.dispatch('alert', {
          message: res.errorMessage.message,
          error: true,
          status: true
        })
        this.$swal.fire({
          icon: 'error',
          title: 'Unable to apply code',
          text: res.errorMessage.message
        })
        this.loadingPromoCode = false
        return
      }

      if (res.data?.data?.discountValue) {
        this.promoCodeBonus = res.data.data.discountValue

        this.promoCodeBonus = Number(this.promoCodeBonus).toFixed(2)

        // Applying used to silently set promoCodeBonus with nothing on
        // screen telling the user it worked (the value only became visible
        // much later, in the trade summary) — this immediate confirmation
        // is the fix for that missing feedback.
        this.$store.dispatch('alert', {
          message: `Promo code applied! You get an extra bonus of ${this.promoCodeBonus}`,
          error: false,
          status: true
        })
      } else {
        this.promoCodeBonus = 0
        this.$swal.fire({
          icon: 'error',
          title: 'Unable to apply code',
          text: res.data.data
        })
      }

      this.loadingPromoCode = false
    },
    async calculateCashbackPoint(amount) {
      const res = await transactions
        .transactions()
        .calculatePointsToEarn(amount)
      if (res.error) {
        return
      }

      this.cashbackPointsToEarn = Number(res.data.data).toFixed(2)
    },
    removeImage(index) {
      this.formData.images.splice(index, 1)
    },
    async initSell() {
      return new Promise((resolve, reject) => {
        this.tradeProgress = 'creating trade'

        const comment = (this.formData.comments || '').trim() || undefined

        const sellCardPayload = [
          {
            variationAmount: this.variationAmount,
            quantity: 1,
            nairaRate: this.priceToReturn
          }
        ]

        giftcard
          .giftcard()
          .sell({
            cardId: this.cardId,
            data: {
              promoCode: this.promoCode || undefined,
              cart: sellCardPayload,
              cardOptionId: this.selectedOptionId,
              cardTypeId: this.selectedCardTypeData._id,
              comment
            }
          })
          .then((res) => {
            if (res.error) {
              this.$store.dispatch('alert', {
                message: res.errorMessage.message,
                error: true,
                status: true
              })
              this.tradeProgress = 'idle'
              reject(res.errorMessage)
              return
            }
            resolve(res.data.data)
          })
          .catch((error) => {
            this.tradeProgress = 'idle'
            reject(error)
          })
      })

      // this.uploadImage = true
    },
    async createSell() {
      this.tradeProgress = 'uploading images'

      await this.initSell().then(async (createdSellData) => {
        if (this.formData.images.length > 0) {
          const res = await transactions.transactions().addImageToTransaction({
            images: this.formData.images,
            trxId: createdSellData._id
          })
          if (res.error) {
            this.$store.dispatch('alert', {
              message:
                res.errorMessage.message ||
                `${res.internalError.message}, please try again`,
              error: true,
              status: true
            })
            this.tradeProgress = 'idle'
            return
          }
        }

        this.tradeProgress = 'idle'
        this.sellCard = {}
        this.$store.dispatch('alert', {
          message: 'Transaction sent! Kindly wait for approval',
          status: true
        })
        this.$router.push('/user/dashboard')
      })
    }
  },
  computed: {
    ...mapState({
      user: (state) => state.user.user,
      siteData: (state) => state.user.siteSettings,
      countrySetting: (state) => state.user.countryCurrency
    }),
    confirmButtonText() {
      switch (this.tradeProgress) {
        case 'idle':
          return ''
        case 'creating trade':
          return 'Creating trade...'
        case 'uploading images':
          return 'Contacting admin...'
        case 'completed':
          return 'Completed'
        default:
          return ''
      }
    },
    amountPrefix() {
      if (this.selectedOptionId) {
        const availableCountry = this.availableCountry.find(
          (e) => e.id === this.selectedOptionId
        )

        return availableCountry?.currencySymbol || ''
      }
      return ''
    },
    selectedCardTypeOptions() {
      if (Object.keys(this.selectedCardTypeData).length > 0) {
        return this.selectedCardTypeData.options
      }
      return []
    },
    selectedCardTypeData() {
      if (this.selectedCardTypeId) {
        const availableCardTypes = this.availableCardTypes.find(
          (op) => op._id === this.selectedCardTypeId
        )

        return availableCardTypes !== undefined ? availableCardTypes : {}
      }
      return {}
    },
    availableCardTypes() {
      if (this.selectedOptionId) {
        return (
          this.cardData.options.find((op) => this.selectedOptionId === op._id)
            ?.cardTypes || []
        )
      }
      return []
    },
    priceToReturn() {
      if (this.variationAmount === '') return numberWithCommas(0)

      const optionMapToASpecificValue = this.selectedCardTypeOptions.find(
        (option) => option.value === Number(this.variationAmount)
      )?.nairaRate

      const optionAvailable = this.selectedCardTypeOptions.find(
        (option) =>
          Number(this.variationAmount) >= option.minimum &&
          Number(this.variationAmount) <= option.maximum
      )

      const priceToReturn =
        optionMapToASpecificValue ||
        (optionAvailable !== undefined
          ? optionAvailable?.nairaRate
          : this.selectedCardTypeOptions.find(
              (option) => option.default === true
            )?.nairaRate ||
            this.selectedCardTypeOptions.find(
              (option) => option.default === true
            )?.value)

      return priceToReturn
    },
    calculatedPrice() {
      if (this.variationAmount === '') return numberWithCommas(0)

      const price = this.variationAmount * this.priceToReturn

      return Number(
        price / this.countrySetting?.[this.userCountry.toLowerCase()]?.nairaRate
      ).toFixed(2)
    },
    availableCountry() {
      if (this.cardData) {
        return this.cardData.options.map((op) => ({
          id: op._id,
          countryName: op.country.name,
          currencySymbol: op?.currency?.symbol,
          currencyLabel: op?.currency?.cc
        }))
      }
      return []
    },
    calculatedPriceBase() {
      if (this.variationAmount === '') return numberWithCommas(0)

      const price = this.variationAmount * this.priceToReturn

      return Number(price).toFixed(2)
    },
    calculatedPriceWithBonus() {
      const calc = Number(this.calculatedPrice) + Number(this.promoCodeBonus)
      return numberWithCommas(calc)
    },
    imagesRender() {
      if (this.formData.images && this.formData.images.length === 0) return []
      return this.formData.images.map((img) => {
        const imgUrl = URL.createObjectURL(img)
        return imgUrl
      })
    },
    cardTypeHint() {
      if (this.selectedCardTypeData && this.selectedCardTypeData.minimumSell) {
        return `You can trade ${this.amountPrefix} ${this.selectedCardTypeData.minimumSell} and above for ${this.selectedCardTypeData.cardType}`
      }
      return ''
    },
    amountInputHint() {
      const minimumSell = this.selectedCardTypeData.minimumSell
      if (minimumSell && this.variationAmount < minimumSell) {
        return `You can trade ${this.amountPrefix} ${minimumSell} and above for ${this.selectedCardTypeData.cardType}`
      }

      if (this.cashbackPointsToEarn) {
        return `Estimated Cashback Points ${this.cashbackPointsToEarn}`
      }

      return ''
    }
  },
  watch: {
    calculatedPrice(oldValue, newValue) {
      this.promoCodeBonus = 0
      if (this.promoCode !== '') {
        this.applyPromoCode()
      }
    },
    calculatedPriceBase(newValue, oldValue) {
      if (oldValue !== newValue && newValue !== 0) {
        this.calculateCashbackPoint(newValue.toString().replace(/,/g, ''))
      }
    }
  }
}
</script>

<style scoped>
/* Custom scrollbar for select dropdowns */
select::-ms-expand {
  display: none;
}

/* Ensure consistent styling across browsers */
input,
select,
textarea {
  font-family: inherit;
}
</style>
